import { useEffect, useRef, type RefObject } from "react";
import { useTranslation } from "@ferrivia/i18n";
import { useKeyboardVisible } from "../../hooks/useKeyboardVisible";
import s from "./ConditionalReveal.module.css";

export interface ConditionalRevealProps {
  show: boolean;
  label: string;
  scrollContainer: RefObject<HTMLElement | null>;
  children: React.ReactNode;
}

/** Wraps one follow-up question, nested under the question whose answer
    gates it. Stays mounted at all times -- `show` only toggles the CSS grid
    track that animates its height to/from zero -- so a question that becomes
    unreachable collapses into its parent instead of popping out of the DOM,
    and a question reachable from the start (resuming mid-section) renders
    already-open with no animation (no prior value for the CSS transition to
    animate from). `inert` drops it out of the tab order and out of the
    accessibility tree while collapsed, matching the clip-hidden chip inputs'
    own approach to "present but not reachable". */
export function ConditionalReveal({ show, label, scrollContainer, children }: ConditionalRevealProps) {
  const { t } = useTranslation("client");
  const rootRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const wasShown = useRef(show);
  const keyboardVisible = useKeyboardVisible();

  useEffect(() => {
    const justRevealed = show && !wasShown.current;
    wasShown.current = show;
    if (!justRevealed || keyboardVisible) return;
    if (!window.matchMedia?.("(prefers-reduced-motion: no-preference)").matches) return;

    const container = scrollContainer.current;
    const inner = innerRef.current;
    if (!container || !inner) return;
    const margin = 16;
    const containerRect = container.getBoundingClientRect();
    const innerTop = inner.getBoundingClientRect().top;
    // scrollHeight, not the (still-collapsing) rect height: the CSS grid
    // track animation means inner's own box is near zero-height at the exact
    // moment `show` flips, well before the transition finishes.
    const overflow = innerTop + inner.scrollHeight - containerRect.bottom;
    if (overflow <= 0) return;
    // Capped to the revealed block's own top edge: a small follow-up (a text
    // field under a "Kyllä") fully fits, so this is the same delta as
    // before, but a repeating group can grow taller than the whole screen
    // once it has items in it, and scrolling to force its bottom into view
    // would blow straight past the "+ Lisää" button the client just
    // revealed -- never scroll further than bringing its own top just below
    // the header.
    const maxDelta = Math.max(0, innerTop - containerRect.top - margin);
    const delta = Math.min(overflow + margin, maxDelta);
    if (delta > 0) {
      container.scrollTo({ top: container.scrollTop + delta, behavior: "smooth" });
    }
  }, [show, keyboardVisible, scrollContainer]);

  return (
    <div className={s.root} data-show={show} ref={rootRef} inert={!show}>
      <div className={s.inner} ref={innerRef}>
        {show ? (
          <span role="status" aria-live="polite" className={s.announcement}>
            {t("questionnaire.followUpAnnounced", "Lisäkysymys: {{label}}", { label })}
          </span>
        ) : null}
        {children}
      </div>
    </div>
  );
}
